import { store as defaultStore } from "../../store/store";
import { logout } from "../../store/user-info/actions";
import { baseServletUrl, baseUrl, contentTypeGet, contentTypeOther } from '../../constants/api'
import { UNAUTHORIZED } from "../../constants/response-status-codes";

export class APIConnector {
    store: any = undefined;

    constructor() {
        this.store = defaultStore;
    }

    /**
     * Fetches data from the specified URL.
     *
     * If `useServletUrl` is true, a headers object must include a "type" attribute,
     * while other attributes are optional.
     * The "type" attribute is crucial as the backend uses this value to retrieve the correct information,
     * since the url sent through is an empty string
     *
     * @param {string} url - The URL to fetch data from.
     * @param {boolean} [useServletUrl=false] - Whether to use a servlet URL.
     * @param {object} [headers={}] - Optional headers to include in the request.
     * @param {string} [contentType] - Optional content type for the request.
     * @returns {Promise<Response>} - The response from the fetch call.
     */
    fetchData = async (url: string, useServletUrl?: boolean, headers?: {}, contentType?: string) : Promise<Response> => {
        const token = sessionStorage.getItem('token');
        const headersHasCookie = headers &&  headers['credentials'] ? headers['credentials'] === 'include' : false;
        if (!!!token && !headersHasCookie) {
            this.store?.dispatch(logout());
            return new Promise((resolve, reject) => {
                reject({ status: UNAUTHORIZED, message: 'Unathorized'})
            });
        }

        const apiUrl = (useServletUrl ? baseServletUrl : baseUrl + url) ;

        const fetchMethod = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Accept: "*/*",
                "Content-Type": contentType ?? contentTypeGet,
                "Authorization": !headersHasCookie ? "Bearer " + JSON.parse(token ?? '{}') : '',
                "bypasscache": "true", // Disable caching on a per query basis. Fixes Absenteeism report not showing latest results.
                ...headers,
            }
        });

        if (await (await fetchMethod).status === UNAUTHORIZED) {
            this.store?.dispatch(logout());
        }

        return fetchMethod;
    };

    sendDataWithHeaders = async (url: string, createNewRecord: boolean, newRecordData: any, headers: {}, useServletUrl?: boolean): Promise<Response> => {
        return await this.sendData(url, createNewRecord, newRecordData, useServletUrl, headers);
    }

    sendData = async (url: string, createNewRecord: boolean, newRecordData: any, useServletUrl?: boolean, headers={}): Promise<Response> => {
        const token = sessionStorage.getItem('token');
        if (!!!token && !url.includes('authLogin')) {
            this.store?.dispatch(logout());
            return Promise.reject({ status: UNAUTHORIZED, message: 'Unathorized' });
        }

        const fetchMethod = await fetch((useServletUrl ? baseServletUrl : baseUrl) + url, {
            method: createNewRecord ? "POST" : "PATCH",
            headers: {
                Accept: "*/*",
                "Content-Type": contentTypeOther,
                "Authorization": "Bearer " + JSON.parse(token ?? '{}'),
                ...headers
            },
            body: JSON.stringify(newRecordData),
        });

        if (await (await fetchMethod).status === UNAUTHORIZED) {
            this.store?.dispatch(logout());
        }

        return fetchMethod;
    };

    deleteData = async (url: string, currentRecordData: any) : Promise<Response> => {
        const token = sessionStorage.getItem('token');
        if (!!!token) {
            this.store?.dispatch(logout());
            return new Promise((resolve, reject) => {
                reject({ status: UNAUTHORIZED, message: 'Unathorized' })
            });
        }

        const fetchMethod = fetch(baseUrl + url, {
            method: 'DELETE',
            headers: {
                'Accept': '*/*',
                'Content-Type': contentTypeOther,
                'Authorization': "Bearer " + JSON.parse(token ?? '{}')
            },
            body: JSON.stringify(currentRecordData)
        });

        if (await (await fetchMethod).status === UNAUTHORIZED) {
            this.store?.dispatch(logout());
        }
        return fetchMethod;
    }

    /**
     * Sends data to endpoints that don't require authentication.
     *
     * @param {string} url - The URL to send data to.
     * @param {any} data - The data to send.
     * @param {boolean} [useServletUrl=false] - Whether to use a servlet URL.
     * @returns {Promise<Response>} - The response from the fetch call.
     */
    sendDataNoAuth = async (url: string, data: any, useServletUrl?: boolean):Promise<Response> => {
        const apiUrl = (useServletUrl ? baseServletUrl : baseUrl) + url;

        return fetch(apiUrl,{
            method: 'POST',
            headers: {
                Accept: "*/*",
                "Content-Type": contentTypeOther
            },
            body:JSON.stringify(data)
        })
    }
}
